// One group of the form (spec §4, §7): its title and description, a 12-column grid for its fields, and for a repeatable
// group one block per instance with "add" below the last and "remove" beside every instance but the first.
import { h } from "./h.js";
import { scope_props } from "./styles.js";
import { REPEAT_MAX, REPEAT_MIN } from "./constants.js";
import { label_of, first_error_text, split_code } from "./messages.js";
import { fields_of, instance_count, read_field, error_path, state_of_field } from "./form_logic.js";
import { field_component } from "./fields/index.js";
import { SiteField } from "./fields/site_field.js";
import { dom_id } from "./fields/shell.js";

// The text of a field's error: a number or a price that fails the rule written on it says `validation_message`; any other refusal of the
// value itself (its pattern, format, range, a site's rule) says `error_message` when the admin wrote one; "required" and the errors that are
// not about the value (an upload that failed) keep the generic text.
const NOT_ABOUT_THE_VALUE = ["required", "validation_failed", "file_upload_failed", "file_read_failed"];
export const error_text_of = (field, codes, lang, messages) => {
    const code = split_code(codes?.[0]).code;
    const own = (text) => (typeof text === "string" && text.trim() ? text.trim() : null);
    if (code === "validation_failed" && own(field?.validation_message)) {
        return own(field.validation_message);
    }
    if (code && !NOT_ABOUT_THE_VALUE.includes(code) && own(field?.error_message)) {
        return own(field.error_message);
    }
    return first_error_text(codes, lang, messages);
};

// what one field gets: the whole story of the value, its state from the conditions and its error text
const render_field = (field, group, index, props) => {
    const { snapshot, actions, form_id, lang, messages, styles, class_names, geo, custom_fields, field_components, max_file_bytes, price_unit } = props;
    // a site may draw a fixed type with its own component (`field_components`); the renderer still keeps the value, the state and the errors
    const site_component = field_components?.[field.type];
    const Component = site_component ? SiteField : field_component(field.type);
    if (!Component) {
        return null;
    }
    const field_state = state_of_field(snapshot.form_state, group, index, field.key);
    // a hidden field takes no room: nothing is rendered, so the grid closes up (spec §7)
    if (!field_state?.visible) {
        return null;
    }
    const path = error_path(group, index, field.key);
    const state_field = fields_of(group).find((item) => item.type === "state");
    return h(Component, {
        key: path,
        field,
        group,
        index,
        path,
        id: dom_id(form_id, path),
        value: read_field(snapshot.values, group, index, field.key),
        on_change: (value) => actions.set_value(group.key, field.key, value, index),
        on_error: (codes) => actions.set_field_error(path, codes),
        error: error_text_of(field, snapshot.errors[path], lang, messages),
        required: !!field_state.required,
        disabled: !!field_state.disabled || snapshot.submitting,
        styles,
        class_names,
        lang,
        messages,
        form_values: snapshot.values,
        custom_fields,
        max_file_bytes,
        price_unit,
        site_component,
        geo,
        state_value: state_field ? read_field(snapshot.values, group, index, state_field.key)?.value : undefined,
        all_cities: !state_field,
    });
};

export const Group = (props) => {
    const { group, snapshot, actions, form_id, lang, messages, styles, class_names } = props;
    if (!snapshot.form_state?.groups?.[group.key]?.visible) {
        return null;
    }
    const scope = (slot, extra) => scope_props({ scope: "group", slot, class_names, styles, extra });
    const count = instance_count(snapshot.values, group);
    const instances = Array.from({ length: group.is_repeatable ? count : 1 }, (item, index) => index);
    const heading_id = dom_id(form_id, `${group.key}-title`);

    const body = (index) => h(
        "div",
        {
            key: index,
            ...(group.is_repeatable ? scope("instance") : {}),
            "data-instance": group.is_repeatable ? String(index) : undefined,
        },
        group.is_repeatable
            ? h(
                "div",
                { className: class_names?.instance_header },
                h("span", { className: class_names?.instance_title }, label_of("instance", lang, messages, { title: group.title || "", number: index + 1 })),
                index > 0
                    ? h("button", {
                        type: "button",
                        ...scope("button", class_names?.remove_button),
                        disabled: snapshot.submitting ? true : undefined,
                        onClick: () => actions.remove_instance(group.key, index),
                    }, label_of("remove_instance", lang, messages))
                    : null,
            )
            : null,
        h("div", scope("grid"), ...fields_of(group).map((field) => render_field(field, group, index, props))),
    );

    return h(
        "section",
        { ...scope("root"), "data-group-key": group.key, "data-repeatable": group.is_repeatable ? "true" : undefined, "aria-labelledby": group.title ? heading_id : undefined },
        group.title ? h("h3", { id: heading_id, ...scope("title") }, group.title) : null,
        group.description ? h("p", scope("description"), group.description) : null,
        ...instances.map(body),
        group.is_repeatable && count >= REPEAT_MIN
            ? h("div", scope("actions"), h("button", {
                type: "button",
                ...scope("button", class_names?.add_button),
                disabled: count >= REPEAT_MAX || snapshot.submitting ? true : undefined,
                onClick: () => actions.add_instance(group.key),
            }, label_of("add_instance", lang, messages)))
            : null,
    );
};

// One instance of a group alone: its fields in the group's grid, with no title, no instance header and no add / remove button.
// For a site that draws the instances of a repeatable group itself (jument's return request: one instance per product the
// visitor chose, with the product card above it). Same values, conditions and errors as Group.
export const GroupInstance = (props) => {
    const { group, index = 0, snapshot, class_names, styles } = props;
    if (!snapshot.form_state?.groups?.[group.key]?.visible) {
        return null;
    }
    const at = group.is_repeatable ? index : 0;
    return h(
        "div",
        { ...scope_props({ scope: "group", slot: "grid", class_names, styles }), "data-group-key": group.key, "data-instance": group.is_repeatable ? String(at) : undefined },
        ...fields_of(group).map((field) => render_field(field, group, at, props)),
    );
};
